import { Collapse, UnstyledButton } from '@mantine/core';
import { IconBell, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { CalendarEvent } from 'src/Components/Calendars/MonthCalendar/MonthCalendar.types';
import useCalendarDays from 'src/Hooks/CalendarLogic/useCalendarDays';
import useEventNavigation from 'src/Hooks/CalendarLogic/useEventNavigation';

import buildListWeeks from './buildListWeeks';
import DayRow from './components/DayRow.component';
import EventCard from './components/EventCard.component';
import FreeWeek from './components/FreeWeek.component';
import OngoingStayBanner from './components/OngoingStayBanner.component';
import styles from './ListCalendar.module.scss';
import { Props } from './ListCalendar.types';

const ListCalendar = ({ selectedDate, events, onDateClick, showPracticalReminders }: Props) => {
  const { t, i18n } = useTranslation();
  const days = useCalendarDays(selectedDate);
  const navigateForEvent = useEventNavigation();
  const navigate = useNavigate();
  const [openWeeks, setOpenWeeks] = useState<Record<string, boolean>>({});

  const weeks = useMemo(
    () =>
      buildListWeeks(
        days.map(day => day.date),
        events ?? []
      ),
    [days, events]
  );

  const handleEventClick = (event: CalendarEvent) => navigateForEvent(event, event.startTime);

  return (
    <div className={styles.container}>
      {weeks.map(({ weekStart, ongoingStays, reminders, days: weekDays, isFree }) => {
        const weekKey = weekStart.format('YYYY-MM-DD');
        const remindersOpen = openWeeks[weekKey] ?? false;

        return (
          <section key={weekKey} className={styles.weekBlock}>
            <header className={styles.weekHeader}>
              {t('planning.listView.weekOf', { defaultValue: 'Semaine du ' })}
              {weekStart.locale(i18n.language).format('D MMMM')}
            </header>

            {ongoingStays.map(stay => (
              <OngoingStayBanner key={stay.localId} stay={stay} onClick={handleEventClick} />
            ))}

            {reminders.length > 0 && showPracticalReminders && (
              <div className={styles.remindersSection}>
                <UnstyledButton
                  className={styles.remindersToggle}
                  onClick={() => setOpenWeeks(prev => ({ ...prev, [weekKey]: !prev[weekKey] }))}
                >
                  <IconBell size={18} />
                  <span className={styles.remindersLabel}>
                    {t('planning.listView.remindersCount', {
                      defaultValue: '{{count}} rappels cette semaine',
                      count: reminders.length,
                    })}
                  </span>
                  {remindersOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </UnstyledButton>
                <Collapse in={remindersOpen}>
                  <ul className={styles.remindersList}>
                    {reminders.map(event => (
                      <li key={event.localId}>
                        <button
                          className={styles.reminderRow}
                          onClick={() => handleEventClick(event)}
                          type="button"
                        >
                          <IconBell size={14} />
                          <span>{event.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </div>
            )}

            {isFree && <FreeWeek onBookStay={() => navigate('new-event')} />}

            {weekDays.map(({ date, markers, events: dayEvents }) => (
              <div key={date.format('YYYY-MM-DD')} className={styles.dayBlock}>
                <DayRow
                  date={date}
                  markers={markers}
                  isToday={date.isSame(dayjs(), 'day')}
                  onDateClick={onDateClick}
                />
                <ul className={styles.eventList}>
                  {dayEvents.map(event => (
                    <li key={event.localId}>
                      <EventCard event={event} onClick={handleEventClick} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
};

export default ListCalendar;
